<template>
  <section class="content white large-padding">
    <form class="hide-for-print" @submit.prevent="handleSubmit" spellcheck="false">
      <TheLoader :active="loading || exporting" :update="true"/>

      <fieldset style="margin: 0;">
        <div class="grid-x grid-padding-x">
          <TheAutoCompleteInput
            v-model.trim="form.code"
            ref="auksjon_input"
            name="Auksjon"
            containerClassName="cell large-2"
            :placeholder="'Velg auksjon'"
            :label="'Tilhørende auksjon'"
            :onInputCallback="onAuksjonSearchInput"
            :onSelectCallback="onAuksjonSearchSelected"
            :debounce="750"
            :minquery="2"
          >
            <div slot="item" slot-scope="props">
              <strong>{{props.item.Auksjon}}</strong>, {{props.item.Beskrivelse}}
              <span v-if="props.item.Auksjon_dato">
                ({{ props.item.Auksjon_dato | avantidate }})
              </span>
            </div>
          </TheAutoCompleteInput>

          <TheSelectInput v-model="selectedDestination" containerClassName="cell large-1" label="Destinasjon" name="destinasjon" :items="uniqueDestinations" :disabled="loading"/>

          <div class="cell auto">
            <label>&nbsp;</label>
            <template v-if="isValidForSubmit">
              <a v-if="!(loading || exporting)" href="javascript:;" @click="handleSubmit()" class="button primary submit tiny">Ok</a>
              <a v-else-if="loading" href="javascript:;" class="button primary submit tiny loading">Ok <span class="loader-spinner"></span></a>
              <a v-else href="javascript:;" class="button primary submit tiny" disabled>Ok</a>

              <template v-if="!objects || objects.length < 1">
                <a href="javascript:;" class="button primary submit tiny" disabled style="margin-left: 0.25rem;">Exporter</a>
              </template>
              <template v-else-if="exporting">
                <a href="javascript:;" class="button primary submit tiny loading" style="margin-left: 0.25rem;">Exporter <span class="loader-spinner"></span></a>
              </template>
              <template v-else>
                <a @click="handleExport()" href="javascript:;" class="button primary submit tiny" style="margin-left: 0.25rem;">Exporter</a>
              </template>

              <template v-if="isValidPrintLabelSubmit">
                <a href="javascript:;" class="button primary submit tiny" @click="handlePrintLabelSubmit()">Skriv ut etiketter</a>
              </template>
              <template v-else>
                <a href="javascript:;" class="button primary submit tiny" disabled>Skriv ut etiketter</a>
              </template>
            </template>
            <template v-else>
              <a href="javascript:;" class="button primary submit tiny" disabled>Ok</a>
            </template>
          </div>
        </div>
      </fieldset>
    </form>

    <div class="content-container flex-column">
      <div class="content-container flex-row" v-if="objects.length < 1">
        <em v-if="!searched" style="font-size: 0.75rem;" >
          Søk med hjelp av skjema oven...
        </em>
        <em v-else style="font-size: 0.75rem;">
          Fant ingen statistikk...
        </em>
      </div>
      <div v-else class="content-container flex-row content-x-scroll content-y-scroll table-results">
        <table class="large fixed sortable sticky-table-header compressed color-rows" style="height: 0">
          <thead>
            <tr class="header">
              <th v-for="(col, index) in columns" :key="col.key + '_' + index">
                {{col.name}}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in filteredObjects" :key="'section-list-' + item.Objektid" class="list">
              <td class="unsortable align-left">{{ item.Objektnr }}</td>
              <td class="unsortable align-left">{{ item.Katalogtekst1 }}</td>
              <td class="unsortable align-left">{{ item.Navn }}</td>
              <td class="unsortable align-left">{{ item.Mobil }}</td>
              <td class="unsortable align-left">{{ item.Antall }}</td>
              <td class="unsortable align-left">{{ formatDate(item.Bet_dato) }}</td>
              <td class="unsortable align-left">{{ item.Destinasjon }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import StatistikkService from '@/services/StatistikkService'
import InstillingerService from '@/services/InstillingerService'
import AuksjonService from '@/services/AuksjonService'
import DateFromTillForm from '@/components/Inputs/DateFromTillForm'
import TheAutoCompleteInput from '@/components/Inputs/TheAutoCompleteInput'
import moment from 'moment'
import fileDownload from 'js-file-download'
import Papa from 'papaparse'

export default {
  name: 'Vinpakkeliste',
  $_veeValidate: {
    validator: 'new'
  },
  components: {
    DateFromTillForm,
    TheAutoCompleteInput
  },
  data () {
    return {
      loading: false,
      exporting: false,
      searched: false,
      objects: [],
      avdDropdown: [],
      sums: {},
      form: {
        code: null
      },
      sort: 'Objektnr',
      dir: 'ASC',
      selectedDestination: ''
    }
  },
  computed: {
    isValidForSubmit: function () {
      return this.form.code
    },
    isValidPrintLabelSubmit: function () {
      return this.selectedDestination !== '' && this.objects.length > 0
    },
    columns: function () {
      const cols = this.$store.getters['vinpakkeliste/columns']
      return cols.filter(c => c.selected)
    },
    uniqueDestinations () {
      const destinations = this.objects.map(item => ({
        key: item.destinasjonId,
        val: item.destinasjonCity
      }))

      // Remove duplicates based on destinasjonId
      const uniqueDestinations = Array.from(
        new Map(destinations.map(item => [item.key, item])).values()
      )

      // insert first line with all objects
      uniqueDestinations.unshift({
        key: '',
        val: 'Alle'
      })

      return uniqueDestinations
    },
    filteredObjects () {
      if (!this.selectedDestination) {
        return this.objects
      }
      return this.objects.filter(item => item.destinasjonId === this.selectedDestination)
    }
  },
  created () {
    this.avdDropdown.unshift({key: this.form.avdelingsid, val: 'Laster...'})

    InstillingerService('avdeling').dropdown()
      .then(response => {
        this.avdDropdown = response.data || []
        this.avdDropdown.unshift({key: null, val: 'Velg avdeling', disabled: false})
      })
      .catch(error => {
        console.log(error)
      })
  },
  methods: {
    handleSubmit: function () {
      if (!this.isValidForSubmit) {
        return
      }

      // Make query
      const query = {}
      if (this.form.code) {
        query.code = this.form.code
      }

      this.searched = false
      this.loading = true
      StatistikkService.loadVinpakkelisteJson(query)
        .then(response => {
          if (response.data) {
            this.objects = response.data
          } else {
            throw new Error('Kunne ikke finne objekter...')
          }
        })
        .catch(error => {
          console.log('Error', error)
          this.$helpers.showErrorAlert('Feil', 'Noe gikk galt, prøv igjen', 'danger')
          this.objects = []
        })
        .then(() => {
          this.loading = false
          this.searched = true
        })
    },

    handlePrintLabelSubmit: function () {
      const items = this.filteredObjects.map(obj => ({
        Objektid: obj.Objektid,
        Objektnr: obj.Objektnr,
        title: obj.Katalogtekst1,
        subtitle: obj.subtitle || ''
      }))

      if (!items) {
        this.$helpers.showErrorAlert('Feil', 'Vennligst velg ett eller flere objekter.')
      } else {
        this.openModal(
          'deliver-objekt-modal',
          {
            kunde: null,
            items: items,
            internalWindow: true
          },
          'split'
        )
      }
    },
    onAuksjonSearchInput: function (query) {
      return AuksjonService.searchInSettings(query)
    },
    onAuksjonSearchSelected: function (item) {
      if (item) {
        this.form.code = item.Auksjon || null
      } else {
        this.form.code = null
      }
    },
    exportVinpakkeliste: function () {
      return new Promise((resolve, reject) => {
        const data = []
        const headers = []

        this.columns.forEach(c => {
          headers.push(c.name)
        })
        data.push(headers)

        this.objects.forEach(obj => {
          const row = []

          this.columns.forEach(c => {
            row.push(obj[c.key] || '')
          })

          data.push(row)
        })

        // Finally make csv and force download...
        const csv = '\ufeff' + Papa.unparse(data, { delimiter: ';' })
        const blob = new Blob([csv], {
          type: 'application/csvcharset=utf-8'
        })

        fileDownload(blob, 'vinpakkeliste-' + moment().format('DDMMYY.Hms') + '.csv')
        resolve()
      })
    },
    handleExport: function () {
      if (this.objects.length < 1) {
        this.$helpers.showErrorAlert('Feil', 'Ingen objekter at skrive ut')
        return
      }

      this.exporting = true
      this.exportVinpakkeliste()
        .then(() => {
          this.exporting = false
        })
    },
    formatDate: function (date) {
      return moment(new Date(date)).format('DD.MM.YYYY HH:mm:ss')
    }
  }
}
</script>
<style scoped>
  table.fixed tbody tr td:after {
    border: none !important;
    background-color: revert !important;
  }

  table.fixed tr td:first-child,
  table.fixed tr th:first-child {
    padding-left: 0;
  }

  table.fixed tr.totals {
    /* border-top: 1px solid #000; */
    /* border-bottom: 3px double #000; */
    -webkit-box-shadow: 0 -1px 0 0 rgb(0 0 0 / 52%), inset 0 -1px 0 0 rgb(0 0 0 / 52%) !important;
    box-shadow: 0 -1px 0 0 rgb(0 0 0 / 52%), inset 0 -1px 0 0 rgb(0 0 0 / 52%) !important;
  }

  table.fixed tr.totals td {
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
  }

  table.fixed tr.totals td:first-child,
  table.fixed tr.sums td:first-child {
    font-weight: bold;
  }

  table.fixed tr.sums td {
    font-size: 0.75rem;
    font-weight: bold;
  }

  table.fixed tr.sums,
  table.fixed tr.breaker {
    border: none !important;
    box-shadow: none !important;
    -webkit-box-shadow: none !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/Admin/Reports/Vinpakkeliste.vue